import React, { useCallback, useEffect, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import Icon from "@mui/material/Icon";
import Menu from "@mui/material/Menu";
import Pagination from "@mui/material/Pagination";
import MenuItem from "@mui/material/MenuItem";
import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Alert from "@mui/material/Alert";
import Snackbar from "@mui/material/Snackbar";
import { fetchSpecialties, searchSpecialties, createSpecialty, updateSpecialty, deleteSpecialty } from "config/apiFunctions";
import { InputAdornment, PaginationItem } from "@mui/material";
import styled from "@emotion/styled";

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 400,
  bgcolor: 'background.paper',
  boxShadow: 24,
  p: 4,
};

export default function Data() {
  const [data, setData] = useState([]);
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [filterValue, setFilterValue] = useState("");
  const itemsPerPage = 10;
  const [alertInfo, setAlertInfo] = useState({ open: false, message: '', severity: 'success' });

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [menuItem, setMenuItem] = useState(null);

  const { control: addControl, handleSubmit: handleAddSubmit, reset: resetAddForm } = useForm({
    defaultValues: {
      name: ""
    }
  });
  const { control: editControl, handleSubmit: handleEditSubmit, reset: resetEditForm } = useForm();

  const showAlert = (message, severity) => {
    setAlertInfo({ open: true, message, severity });
  };

  const openMenu = (event, item) => {
    setMenuAnchorEl(event.currentTarget);
    setMenuItem(item);
  };

  const CustomPaginationItem = styled(PaginationItem)(({ theme }) => ({
    '&.Mui-selected': {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      '&:hover': {
        backgroundColor: theme.palette.primary.dark,
      },
    },
  }));

  const closeMenu = () => {
    setMenuAnchorEl(null);
    setMenuItem(null);
  };

  const createRows = useCallback((data, currentPage) => {
    return data.map((item, index) => ({
      id: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {(currentPage - 1) * itemsPerPage + index + 1}
        </MDTypography>
      ),
      name: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {item.name || "-"}
        </MDTypography>
      ),
      createdAt: (
        <MDTypography variant="caption" color="text" fontWeight="medium">
          {item.createdAt ? new Date(item.createdAt).toLocaleDateString('uz-UZ') : "-"}
        </MDTypography>
      ),
      action: (
        <div>
          <Icon
            sx={{ cursor: "pointer", fontWeight: "bold" }}
            fontSize="small"
            onClick={(event) => openMenu(event, item)}
          >
            more_vert
          </Icon>
        </div>
      ),
    }));
  }, [itemsPerPage, openMenu]);

  const fetchSpecialtiesData = useCallback(async (pageNumber = 1, filter = "") => {
    setLoading(true);
    const user = localStorage.getItem("user");
    const token = user ? JSON.parse(user).token : null;

    try {
      let res;
      if (filter.trim()) {
        res = await searchSpecialties(filter, token);
      } else {
        res = await fetchSpecialties(token);
      }
      console.log('====================================');
      console.log(res);
      console.log('====================================');
      if (res.success) {
        const specialtiesData = res.data.data || [];

        setData(specialtiesData);
        setTotal(specialtiesData.length);

        // Simple pagination for client-side data
        const startIndex = (pageNumber - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        const paginatedData = specialtiesData.slice(startIndex, endIndex);

        const totalPages = Math.ceil(specialtiesData.length / itemsPerPage);
        setTotalPages(totalPages);
        setPage(pageNumber);

        const newRows = createRows(paginatedData, pageNumber);
        setRows(newRows);
      } else {
        showAlert(res.error?.data?.message || "Mutahasisliklarni yuklashda xatolik yuz berdi", 'error');
        setRows([]);
        setData([]);
      }
    } catch (error) {
      console.error("Error fetching specialties:", error);
      showAlert("Mutahasisliklarni yuklashda xatolik yuz berdi", 'error');
      setRows([]);
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [itemsPerPage, showAlert]);

  useEffect(() => {
    fetchSpecialtiesData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseAlert = (event, reason) => {
    if (reason === 'clickaway') {
      return;
    }
    setAlertInfo({ ...alertInfo, open: false });
  };

  const handleDeleteConfirm = async () => {
    if (!selectedItem) return;

    const user = localStorage.getItem("user");
    const token = user ? JSON.parse(user).token : null;

    try {
      const res = await deleteSpecialty(selectedItem.id, token);
      if (res.success) {
        showAlert("Mutahasislik muvaffaqiyatli o'chirildi", 'success');
        fetchSpecialtiesData(page, filterValue);
      } else {
        showAlert("Mutahasislikni o'chirishda xatolik yuz berdi", 'error');
      }
    } catch (error) {
      console.error("Error deleting specialty:", error);
      showAlert("Mutahasislikni o'chirishda xatolik yuz berdi", 'error');
    } finally {
      setDeleteModalOpen(false);
      setSelectedItem(null);
    }
  };

  const onEditSubmit = async (formData) => {
    if (!selectedItem) return;

    const user = localStorage.getItem("user");
    const token = user ? JSON.parse(user).token : null;

    try {
      const res = await updateSpecialty(selectedItem.id, formData.name, token);
      if (res.success) {
        showAlert("Mutahasislik muvaffaqiyatli yangilandi", 'success');
        fetchSpecialtiesData(page, filterValue);
        setEditModalOpen(false);
        resetEditForm();
      } else {
        showAlert("Mutahasislikni yangilashda xatolik yuz berdi", 'error');
      }
    } catch (error) {
      console.error("Error updating specialty:", error);
      showAlert("Mutahasislikni yangilashda xatolik yuz berdi", 'error');
    }
  };

  const onAddSubmit = async (formData) => {
    const user = localStorage.getItem("user");
    const token = user ? JSON.parse(user).token : null;

    try {
      const res = await createSpecialty(formData.name, token);
      if (res.success) {
        showAlert("Mutahasislik muvaffaqiyatli qo'shildi", 'success');
        fetchSpecialtiesData(page, filterValue);
        setAddModalOpen(false);
        resetAddForm();
      } else {
        showAlert("Mutahasislik qo'shishda xatolik yuz berdi", 'error');
      }
    } catch (error) {
      console.error("Error creating specialty:", error);
      showAlert("Mutahasislik qo'shishda xatolik yuz berdi", 'error');
    }
  };

  const handleFilterChange = (event) => {
    const value = event.target.value;
    setFilterValue(value);
    fetchSpecialtiesData(1, value);
  };

  const handlePageChange = (event, value) => {
    fetchSpecialtiesData(value, filterValue);
  };

  const columns = [
    { Header: "T/r", accessor: "id", width: "10%", align: "left" },
    { Header: "Nomi", accessor: "name", align: "left" },
    { Header: "Yaratilgan sana", accessor: "createdAt", align: "center" },
    { Header: "Amal", accessor: "action", align: "center" },
  ];

  const FilterInput = (
    <MDBox p={2}>
      <TextField
        fullWidth
        variant="outlined"
        placeholder="Mutahasislik bo'yicha qidirish..."
        value={filterValue}
        onChange={handleFilterChange}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Icon>search</Icon>
            </InputAdornment>
          ),
        }}
      />
    </MDBox>
  );

  const PaginationComponent = (
    <MDBox p={2} display="flex" justifyContent="center">
      <Pagination
        count={totalPages}
        page={page}
        onChange={handlePageChange}
        color="primary"
        renderItem={(item) => <CustomPaginationItem {...item} />}
      />
    </MDBox>
  );

  const AddButton = (
    <MDBox p={2} display="flex" justifyContent="flex-end">
      <Button
        variant="contained"
        color="primary"
        startIcon={<Icon>add</Icon>}
        onClick={() => setAddModalOpen(true)}
        sx={{ backgroundColor: "#1A73E8", color: "#fff" }}
      >
        Yangi Mutahasislik
      </Button>
    </MDBox>
  );

  const MenuComponent = (
    <Menu
      anchorEl={menuAnchorEl}
      open={Boolean(menuAnchorEl)}
      onClose={closeMenu}
    >
      <MenuItem onClick={() => {
        setSelectedItem(menuItem);
        resetEditForm({ name: menuItem.name });
        setEditModalOpen(true);
        closeMenu();
      }}>
        <Icon>edit</Icon>
        <span style={{ marginLeft: 8 }}>Tahrirlash</span>
      </MenuItem>
      <MenuItem onClick={() => {
        setSelectedItem(menuItem);
        setDeleteModalOpen(true);
        closeMenu();
      }}>
        <Icon>delete</Icon>
        <span style={{ marginLeft: 8 }}>O'chirish</span>
      </MenuItem>
    </Menu>
  );

  const DeleteModal = () => (
    <Modal open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
      <Box sx={modalStyle}>
        <MDTypography variant="h6" component="h2" mb={2}>
          Mutahasislikni o'chirish
        </MDTypography>
        <MDTypography sx={{ mt: 2 }}>
          Haqiqatan ham "{selectedItem?.name}" mutahasisligini o'chirmoqchimisiz?
        </MDTypography>
        <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
          <Button variant="outlined" onClick={() => setDeleteModalOpen(false)} sx={{ color: "#1A73E8" }}>
            Bekor qilish
          </Button>
          <Button variant="contained" color="error" onClick={handleDeleteConfirm} sx={{ backgroundColor: "#1A73E8", color: "#fff" }}>
            O'chirish
          </Button>
        </MDBox>
      </Box>
    </Modal>
  );

  const AddModal = () => (
    <Modal open={addModalOpen} onClose={() => setAddModalOpen(false)}>
      <Box sx={modalStyle}>
        <MDTypography variant="h6" component="h2" mb={2}>
          Yangi Mutahasislik Qo'shish
        </MDTypography>
        <form onSubmit={handleAddSubmit(onAddSubmit)}>
          <Controller
            name="name"
            control={addControl}
            rules={{ required: "Mutahasislik nomi kiritilishi shart" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                label="Mutahasislik nomi"
                variant="outlined"
                margin="normal"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" color="secondary" sx={{ color: "#1A73E8" }} onClick={() => setAddModalOpen(false)}>
              Bekor qilish
            </Button>
            <Button type="submit" sx={{ backgroundColor: "#1A73E8", color: "#fff" }} variant="contained" color="primary">
              Qo'shish
            </Button>
          </MDBox>
        </form>
      </Box>
    </Modal>
  );

  const EditModal = () => (
    <Modal open={editModalOpen} onClose={() => setEditModalOpen(false)}>
      <Box sx={modalStyle}>
        <MDTypography variant="h6" component="h2" mb={2}>
          Mutahasislikni Tahrirlash
        </MDTypography>
        <form onSubmit={handleEditSubmit(onEditSubmit)}>
          <Controller
            name="name"
            control={editControl}
            rules={{ required: "Mutahasislik nomi kiritilishi shart" }}
            render={({ field, fieldState: { error } }) => (
              <TextField
                {...field}
                fullWidth
                label="Mutahasislik nomi"
                variant="outlined"
                margin="normal"
                error={!!error}
                helperText={error?.message}
              />
            )}
          />
          <MDBox mt={3} display="flex" justifyContent="flex-end" gap={2}>
            <Button variant="outlined" onClick={() => setEditModalOpen(false)} sx={{ color: "#1A73E8" }}>
              Bekor qilish
            </Button>
            <Button type="submit" variant="contained" color="primary" sx={{ backgroundColor: "#1A73E8", color: "#fff" }}>
              Saqlash
            </Button>
          </MDBox>
        </form>
      </Box>
    </Modal>
  );

  const AlertComponent = (
    <Snackbar open={alertInfo.open} autoHideDuration={6000} onClose={handleCloseAlert}>
      <Alert onClose={handleCloseAlert} severity={alertInfo.severity} sx={{ width: '100%' }}>
        {alertInfo.message}
      </Alert>
    </Snackbar>
  );

  return {
    columns,
    rows,
    FilterInput,
    Pagination: PaginationComponent,
    DeleteModal,
    EditModal,
    AddModal,
    Menu: MenuComponent,
    AddButton,
    AlertComponent
  };
} 